import React, { useState, useEffect } from 'react';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Phone, 
  Mail, 
  Calendar, 
  User, 
  Building2, 
  Euro,
  Plus,
  Save,
  X,
  Trash2,
  MessageSquare,
  Clock,
  Pencil,
  AlertTriangle
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { CRMLead, useCRMLeads } from '@/hooks/useCRMLeads';
import { useProfiles } from '@/hooks/useProfiles';
import { useAuth } from '@/hooks/useAuth';
import ProfilesCombobox from '@/components/crm/ProfilesCombobox';
import { TruckCalculatorDetails } from '@/components/crm/TruckCalculatorDetails';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface LeadDetailModalProps {
  lead: CRMLead;
  onClose: () => void;
  onLeadDeleted?: () => void;
  onLeadUpdated?: () => void;
}

// Label mappings for intake quickscan fields
const FIELD_LABELS = {
  best_time: {
    ochtend: "Ochtend (9-12u)",
    middag: "Middag (12-17u)", 
    avond: "Avond (17-20u)"
  },
  role: {
    eigenaar: "Eigenaar/directeur",
    facility: "Facility manager",
    inkoop: "Inkoop",
    technisch: "Technisch",
    anders: "Anders"
  },
  branch: {
    industrie: "Industrie/productie",
    kantoren: "Kantoren/dienstverlening",
    retail: "Retail/horeca",
    zorg: "Zorg",
    onderwijs: "Onderwijs",
    overheid: "Overheid",
    logistiek: "Logistiek/transport",
    anders: "Anders"
  },
  region: {
    noord_holland: "Noord-Holland",
    zuid_holland: "Zuid-Holland",
    utrecht: "Utrecht",
    gelderland: "Gelderland",
    overijssel: "Overijssel",
    flevoland: "Flevoland",
    friesland: "Friesland",
    groningen: "Groningen",
    drenthe: "Drenthe",
    noord_brabant: "Noord-Brabant",
    limburg: "Limburg",
    zeeland: "Zeeland"
  },
  company_size: {
    klein: "Klein (1-50 werknemers)",
    middel: "Middel (51-250 werknemers)",
    groot: "Groot (250+ werknemers)"
  },
  connection_type: {
    kleinverbruik: "Kleinverbruik (tot 3x80A)",
    grootverbruik: "Grootverbruik",
    onbekend: "Onbekend"
  },
  pain_points: {
    verzwaring_uitgesteld: "Netuitbreiding uitgesteld",
    cap_problemen: "Capaciteitsproblemen",
    productiestops: "Productiestops door energie",
    nieuwe_elektrificatie: "Nieuwe elektrificatie",
    hoge_energiekosten: "Hoge energiekosten",
    duurzaamheidsdoelen: "Duurzaamheidsdoelen",
    regelgeving: "Regelgeving/compliance"
  },
  flex_options: {
    opslag_mogelijk: "Opslag mogelijk",
    opslag_aanwezig: "Opslag aanwezig",
    ev_laders: "EV-laders",
    warmtepompen: "Warmtepompen",
    productie_flex: "Flexibele productie",
    pv_mogelijk: "PV mogelijk",
    pv_aanwezig: "PV aanwezig",
    geen_idee: "Geen idee"
  },
  urgency: {
    "0-3_maanden": "0-3 maanden",
    "3-12_maanden": "3-12 maanden",
    meer_dan_jaar: "Meer dan een jaar",
    orienterend: "Oriënterend"
  },
  data_available: {
    ja: "Ja, beschikbaar",
    nee: "Nee, niet beschikbaar",
    onbekend: "Onbekend"
  },
  budget_ready: {
    ja: "Ja, budget gereed",
    bezig: "Bezig met budgettering",
    nog_niet: "Nog niet"
  }
};

const labelFor = (map: Record<string, string>, value: string | undefined) => {
  if (!value) return 'Niet opgegeven';
  return map[value] || value;
};

const LEAD_STAGES = [
  { value: 'nieuw', label: 'Nieuw' },
  { value: 'contact_opgenomen', label: 'Contact Opgenomen' },
  { value: 'gekwalificeerd', label: 'Gekwalificeerd' },
  { value: 'voorstel', label: 'Voorstel' },
  { value: 'onderhandeling', label: 'Onderhandeling' },
  { value: 'gewonnen', label: 'Gewonnen' },
  { value: 'verloren', label: 'Verloren' }
];

const LeadDetailModal: React.FC<LeadDetailModalProps> = ({ lead, onClose, onLeadDeleted, onLeadUpdated }) => {
  const { updateLead, addNote, updateNote, deleteNote, deleteLead } = useCRMLeads();
  const { profiles, loading: profilesLoading } = useProfiles();
  const { userRole, user } = useAuth();
  const [newNote, setNewNote] = useState('');
  const [nextActionDate, setNextActionDate] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [quickscanDetails, setQuickscanDetails] = useState<any>(null);
  const [loadingQuickscan, setLoadingQuickscan] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  
  // Note editing state
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteBody, setEditNoteBody] = useState('');

  // Check if user can assign leads (only super_admin and admin)
  const canAssignLeads = userRole === 'super_admin' || userRole === 'admin';

  useEffect(() => {
    setNextActionDate(lead.next_action_date ? format(new Date(lead.next_action_date), 'yyyy-MM-dd') : '');
    
    // Load detailed quickscan data if available
    if (lead.origin_submission_id && (lead.origin_type === 'quickscan' || lead.origin_type === 'quickscan_submission' || lead.origin_type === 'intake_quickscan' || lead.origin_type === 'hbe_calculator_submission')) {
      loadQuickscanDetails();
    }
  }, [lead]);

  const loadQuickscanDetails = async () => {
    if (!lead.origin_submission_id) return;
    
    try {
      setLoadingQuickscan(true);
      
      if (lead.origin_type === 'intake_quickscan') {
        // Load ALL intake lead details
        const { data } = await supabase
          .from('intake_leads')
          .select('*')
          .eq('scan_id', lead.origin_submission_id)
          .single();
        
        setQuickscanDetails(data);
      } else if (lead.origin_type === 'hbe_calculator_submission') {
        // Load HBE calculator submission details
        const { data } = await supabase
          .from('hbe_calculator_submissions')
          .select('*')
          .eq('id', lead.origin_submission_id)
          .single();
        
        setQuickscanDetails(data);
      } else {
        // Load quickscan submission with ALL related data
        const [
          { data: submission },
          { data: priorities },
          { data: solutions },
          { data: expansions },
          { data: expansionDetails },
          { data: peakHours },
          { data: recommendations }
        ] = await Promise.all([
          supabase.from('quickscan_submissions').select('*').eq('id', lead.origin_submission_id).single(),
          supabase.from('quickscan_priorities').select('*').eq('submission_id', lead.origin_submission_id),
          supabase.from('quickscan_current_solutions').select('*').eq('submission_id', lead.origin_submission_id),
          supabase.from('quickscan_expansion_plans').select('*').eq('submission_id', lead.origin_submission_id),
          supabase.from('quickscan_expansion_details').select('*').eq('submission_id', lead.origin_submission_id),
          supabase.from('quickscan_energy_peak_hours').select('*').eq('submission_id', lead.origin_submission_id),
          supabase.from('quickscan_recommendations').select('*').eq('submission_id', lead.origin_submission_id)
        ]);

        setQuickscanDetails({
          ...submission,
          priorities: priorities || [],
          current_solutions: solutions || [],
          expansion_plans: expansions || [],
          expansion_details: expansionDetails || [],
          peak_hours: peakHours || [],
          recommendations: recommendations || []
        });
      }
    } catch (error) {
      console.error('Error loading quickscan details:', error);
    } finally {
      setLoadingQuickscan(false);
    }
  };

  const handleStageChange = async (newStage: string) => {
    await updateLead(lead.id, { stage: newStage as CRMLead['stage'] });
  };

  const handleAddNote = async () => {
    if (newNote.trim()) {
      await addNote(lead.id, newNote);
      setNewNote('');
    }
  };

  const handleEditNote = (note: { id: string; body: string }) => {
    setEditingNoteId(note.id);
    setEditNoteBody(note.body);
  };

  const handleSaveEditNote = async () => {
    if (editingNoteId && editNoteBody.trim()) {
      await updateNote(editingNoteId, editNoteBody);
      setEditingNoteId(null);
      setEditNoteBody('');
    }
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditNoteBody('');
  };

  const handleDeleteNote = async (noteId: string) => {
    await deleteNote(noteId);
  };

  const handleOwnerChange = async (ownerId: string | null) => {
    await updateLead(lead.id, { owner_id: ownerId });
    onLeadUpdated?.();
  };

  const handleSaveDetails = async () => {
    await updateLead(lead.id, {
      next_action_date: nextActionDate || null
    });
    setIsEditing(false);
    onLeadUpdated?.();
  };

  const handleDelete = async () => {
    const success = await deleteLead(lead.id);
    if (success) {
      onLeadDeleted?.(); // Notify parent that lead was deleted
      onClose(); // Close modal
    }
  };

  const displayName = lead.organization?.name || 
                     lead.quickscan_data?.company_name || 
                     (lead.contact ? `${lead.contact.first_name || ''} ${lead.contact.last_name || ''}`.trim() : null) ||
                     'Onbekende lead';

  const displayEmail = lead.contact?.email || lead.quickscan_data?.email;

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'quickscan': return 'default';
      case 'webform': return 'secondary';
      case 'website': return 'secondary';
      case 'hbe': return 'secondary';
      case 'truck_calculator': return 'default';
      case 'referral': return 'outline';
      default: return 'outline';
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'hbe': return '🚗 HBE Calculator';
      case 'truck_calculator': return '🚛 Diesel + HBE';
      case 'quickscan': return 'Quickscan';
      case 'webform': return 'Webformulier';
      case 'website': return `Website${lead.meta?.label ? ` · ${lead.meta.label}` : ''}`;
      case 'referral': return 'Verwijzing';
      default: return source;
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl sm:max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">
                {displayName}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-1">
                Lead Details & Management
                <Badge variant={getSourceColor(lead.source)} className="ml-2">
                  {getSourceLabel(lead.source)}
                </Badge>
              </DialogDescription>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Lead verwijderen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Deze actie kan niet ongedaan gemaakt worden. De lead wordt permanent verwijderd.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuleren</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Verwijderen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status & Quick Actions */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Status:</span>
                <Select value={lead.stage} onValueChange={handleStageChange}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_STAGES.map(stage => (
                      <SelectItem key={stage.value} value={stage.value}>
                        {stage.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-gray-600">
                {lead.created_via !== 'contactformulier' && <span>Score: {lead.score}</span>}
                <span>Aangemaakt: {format(new Date(lead.created_at), 'dd MMM yyyy', { locale: nl })}</span>
                {lead.next_action_date && (
                  <span className={new Date(lead.next_action_date) < new Date() ? 'text-red-600 font-medium' : ''}>
                    Volgende actie: {format(new Date(lead.next_action_date), 'dd MMM yyyy', { locale: nl })}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {displayEmail && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`mailto:${displayEmail}`, '_blank')}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </Button>
              )}
              {lead.contact?.phone && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(`tel:${lead.contact?.phone}`, '_blank')}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Bellen
                </Button>
              )}
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="info" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Lead Informatie
              </TabsTrigger>
              <TabsTrigger value="notes" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Notities
              </TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Lead Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Lead Informatie</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Bron:</span>
                        <p>{lead.source}</p>
                      </div>
                      {lead.created_via !== 'contactformulier' && (
                        <div>
                          <span className="font-medium text-gray-700">Score:</span>
                          <p className="font-semibold text-green-600">{lead.score}</p>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-gray-700">Email:</span>
                        <p>{displayEmail || 'Niet beschikbaar'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Telefoon:</span>
                        <p>{lead.contact?.phone || 'Niet beschikbaar'}</p>
                      </div>
                    </div>
                    
                    {/* Lead Assignment - Direct Edit */}
                    <div className="pt-3 border-t">
                      <div className="space-y-2">
                        <span className="font-medium text-gray-700">Toegewezen aan:</span>
                        {canAssignLeads ? (
                          <ProfilesCombobox
                            profiles={profiles}
                            value={lead.owner_id}
                            onValueChange={handleOwnerChange}
                            placeholder="Selecteer eigenaar..."
                            showAssignmentPlaceholder={true}
                          />
                        ) : (
                          <p className="text-sm">
                            {profiles.find(p => p.id === lead.owner_id)?.display_name || 
                             profiles.find(p => p.id === lead.owner_id)?.email || 
                             'Niet toegewezen'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Next Action Date - Direct Edit */}
                    <div className="pt-3 border-t">
                      <div className="space-y-2">
                        <span className="font-medium text-gray-700">Volgende actie datum:</span>
                        {canAssignLeads ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="date"
                              value={nextActionDate}
                              onChange={(e) => setNextActionDate(e.target.value)}
                              className="w-auto"
                            />
                            <Button
                              size="sm"
                              onClick={async () => {
                                await updateLead(lead.id, { 
                                  next_action_date: nextActionDate || null 
                                });
                              }}
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm">
                            {lead.next_action_date 
                              ? format(new Date(lead.next_action_date), 'dd MMM yyyy', { locale: nl })
                              : 'Niet ingesteld'
                            }
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {lead.summary && (
                      <div className="pt-3 border-t">
                        <span className="font-medium text-gray-700">Samenvatting:</span>
                        <p className="text-sm mt-1">{lead.summary}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Notes Section */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Snelle notitie</CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setActiveTab('notes')}
                      className="text-xs text-muted-foreground hover:text-primary"
                    >
                      Bekijk alle notities →
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Textarea
                      placeholder="Voeg een notitie toe..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="min-h-[100px]"
                    />
                    <Button onClick={handleAddNote} disabled={!newNote.trim()}>
                      <Plus className="h-4 w-4 mr-2" />
                      Notitie toevoegen
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="notes" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Add Note */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Notitie toevoegen</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Textarea
                      placeholder="Voeg een notitie toe..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="min-h-[100px]"
                    />
                    <Button onClick={handleAddNote} disabled={!newNote.trim()}>
                      <Plus className="h-4 w-4 mr-2" />
                      Notitie toevoegen
                    </Button>
                  </CardContent>
                </Card>

                {/* All Notes */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Alle notities</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {lead.notes && lead.notes.length > 0 ? (
                      <div className="space-y-3 max-h-[300px] overflow-y-auto">
                        {lead.notes
                          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                          .map((note) => (
                          <div key={note.id} className="border-l-4 border-blue-200 pl-4 py-2 bg-muted/50 rounded-r">
                            {editingNoteId === note.id ? (
                              <div className="space-y-2">
                                <Textarea
                                  value={editNoteBody}
                                  onChange={(e) => setEditNoteBody(e.target.value)}
                                  className="min-h-[80px]"
                                />
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={handleSaveEditNote}>
                                    <Save className="h-3 w-3 mr-1" />
                                    Opslaan
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={handleCancelEditNote}>
                                    Annuleren
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <p className="text-sm text-foreground whitespace-pre-wrap">{note.body}</p>
                                <div className="flex items-center justify-between mt-2">
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Clock className="h-3 w-3" />
                                    <span>{format(new Date(note.created_at), 'dd MMM yyyy HH:mm', { locale: nl })}</span>
                                  </div>
                                  {note.created_by === user?.id && (
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => handleEditNote(note)}
                                      >
                                        <Pencil className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                        onClick={() => handleDeleteNote(note.id)}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">Nog geen notities toegevoegd.</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          {/* Contactformulier Details Section */}
          {lead.created_via === 'contactformulier' && (
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Contactformulier Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <span className="text-sm font-medium text-blue-600">Naam</span>
                    <p className="text-sm font-semibold text-blue-900 mt-1">
                      {lead.meta?.contact_form_data?.name || lead.contact?.first_name || 'Niet beschikbaar'}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <span className="text-sm font-medium text-green-600">Email</span>
                    <p className="text-sm font-semibold text-green-900 mt-1">
                      {lead.meta?.contact_form_data?.email || lead.contact?.email || 'Niet beschikbaar'}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <span className="text-sm font-medium text-purple-600">Telefoon</span>
                    <p className="text-sm font-semibold text-purple-900 mt-1">
                      {lead.meta?.contact_form_data?.phone || lead.contact?.phone || 'Niet beschikbaar'}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <span className="text-sm font-medium text-orange-600">Bedrijf</span>
                    <p className="text-sm font-semibold text-orange-900 mt-1">
                      {lead.meta?.contact_form_data?.company || lead.organization?.name || 'Niet beschikbaar'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                    <span className="text-sm font-medium text-gray-600">Bericht</span>
                    <p className="text-sm font-semibold text-gray-900 mt-1">
                      {lead.meta?.contact_form_data?.message || 'Geen bericht'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Websiteformulier (migratie 100, ADR 0026): wat de invuller op greenvision.energy invulde */}
          {lead.source === 'website' && (
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Websiteformulier{lead.meta?.label ? `: ${lead.meta.label}` : ''}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {lead.meta?.matched_existing_contact && (
                  <Alert>
                    <AlertDescription>
                      Gekoppeld aan een bestaand contact met hetzelfde e-mailadres. Naam, telefoon en organisatie van dat contact zijn niet aangepast; hieronder staat wat er op de site is ingevuld.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    ['Naam (ingevuld)', lead.meta?.name],
                    ['Telefoon (ingevuld)', lead.meta?.phone],
                    ['Bedrijf (ingevuld)', lead.meta?.company],
                    ['Pagina', lead.meta?.page],
                    ['Herkomst', lead.meta?.referrer],
                    ['Campagne', [lead.meta?.utm?.source, lead.meta?.utm?.medium, lead.meta?.utm?.campaign].filter(Boolean).join(' / ')],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k as string} className="bg-muted/50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-muted-foreground">{k}</span>
                      <p className="font-medium break-words mt-1">{v as string}</p>
                    </div>
                  ))}
                </div>
                {lead.meta?.message && (
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <span className="text-xs font-medium text-muted-foreground">Bericht</span>
                    <p className="whitespace-pre-wrap mt-1">{lead.meta.message}</p>
                  </div>
                )}
                {lead.meta?.data && Object.keys(lead.meta.data).length > 0 && (
                  <details className="bg-muted/50 p-3 rounded-lg">
                    <summary className="cursor-pointer text-xs font-medium text-muted-foreground">Invoer en uitkomst van het formulier</summary>
                    <pre className="text-xs mt-2 overflow-x-auto">{JSON.stringify(lead.meta.data, null, 2)}</pre>
                  </details>
                )}
              </CardContent>
            </Card>
          )}

          {/* Truck Calculator Details Section */}
          {lead.origin_type === 'truck_calculator_submission' && lead.truck_data && (
            <TruckCalculatorDetails details={lead.truck_data} />
          )}

          {/* HBE Calculator Details Section */}
          {lead.origin_type === 'hbe_calculator_submission' && quickscanDetails && (
            <>
            {/* Warning for empty/test data */}
            {quickscanDetails.total_kwh === 0 && (
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  ⚠️ Deze lead bevat lege/testdata. Controleer of de originele formulier-inzending correct is afgerond.
                </AlertDescription>
              </Alert>
            )}
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  🚗 HBE Calculator Rapportage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Contact & Company Information */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Contactgegevens & Bedrijfsinformatie</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-blue-600">Bedrijfsnaam</span>
                      <p className="text-sm font-semibold text-blue-900 mt-1">
                        {quickscanDetails.company_name || 'Niet opgegeven'}
                      </p>
                    </div>
                    <div className="bg-indigo-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-indigo-600">Contactpersoon</span>
                      <p className="text-sm font-semibold text-indigo-900 mt-1">
                        {quickscanDetails.contact_name || 'Niet opgegeven'}
                      </p>
                    </div>
                    <div className="bg-cyan-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-cyan-600">Email</span>
                      <p className="text-sm font-semibold text-cyan-900 mt-1">
                        {quickscanDetails.email || 'Niet opgegeven'}
                      </p>
                    </div>
                    <div className="bg-teal-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-teal-600">Telefoon</span>
                      <p className="text-sm font-semibold text-teal-900 mt-1">
                        {quickscanDetails.phone || 'Niet opgegeven'}
                      </p>
                    </div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <span className="text-sm font-medium text-green-600">Wil voorstel ontvangen?</span>
                    <Badge className="mt-2" variant={quickscanDetails.wants_proposal ? 'default' : 'secondary'}>
                      {quickscanDetails.wants_proposal ? '✅ JA - WIL VOORSTEL' : 'Nee'}
                    </Badge>
                  </div>
                </div>

                {/* Vehicles Overview */}
                {quickscanDetails.vehicles_data && Array.isArray(quickscanDetails.vehicles_data) && quickscanDetails.vehicles_data.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-gray-900 border-b pb-1">Voertuigen Overzicht</h3>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="space-y-3">
                        {quickscanDetails.vehicles_data.map((vehicle: any, index: number) => (
                          <div key={index} className="bg-white p-3 rounded-lg border border-purple-200">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                              <div>
                                <span className="text-xs text-purple-600 font-medium">Type</span>
                                <p className="font-semibold text-purple-900">{vehicle.vehicle_type || 'Onbekend'}</p>
                              </div>
                              <div>
                                <span className="text-xs text-purple-600 font-medium">Aantal</span>
                                <p className="font-semibold text-purple-900">{vehicle.aantal || 0}</p>
                              </div>
                              <div>
                                <span className="text-xs text-purple-600 font-medium">kWh/km</span>
                                <p className="font-semibold text-purple-900">{vehicle.kwh_per_km || 'N/A'}</p>
                              </div>
                              <div>
                                <span className="text-xs text-purple-600 font-medium">km/jaar</span>
                                <p className="font-semibold text-purple-900">{vehicle.km_per_jaar?.toLocaleString('nl-NL') || 'N/A'}</p>
                              </div>
                              <div>
                                <span className="text-xs text-purple-600 font-medium">Totaal kWh/jaar</span>
                                <p className="font-semibold text-purple-900">
                                  {vehicle.kwh_per_km && vehicle.km_per_jaar 
                                    ? `${(vehicle.kwh_per_km * vehicle.km_per_jaar).toLocaleString('nl-NL', { maximumFractionDigits: 0 })} kWh`
                                    : 'N/A'
                                  }
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="bg-purple-100 p-2 rounded text-center">
                          <span className="text-sm font-semibold text-purple-900">
                            Totaal: {quickscanDetails.vehicles_data.reduce((sum: number, v: any) => sum + (parseInt(v.aantal) || 0), 0)} voertuigen
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Energy Mix & Prices */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Energiemix & Prijzen</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-yellow-600">Netstroom %</span>
                      <p className="text-sm font-semibold text-yellow-900 mt-1">
                        {quickscanDetails.netstroom_pct || 0}%
                      </p>
                    </div>
                    <div className="bg-amber-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-amber-600">Zonnestroom %</span>
                      <p className="text-sm font-semibold text-amber-900 mt-1">
                        {quickscanDetails.zonnestroom_pct || 0}%
                      </p>
                    </div>
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-orange-600">Prijs ERE</span>
                      <p className="text-sm font-semibold text-orange-900 mt-1">
                        €{(quickscanDetails.prijs_ere || 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-red-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-red-600">Extra kosten</span>
                      <p className="text-sm font-semibold text-red-900 mt-1">
                        €{(quickscanDetails.extra_kosten || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Calculated Results */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Berekende Resultaten</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-sky-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-sky-600">Totaal kWh</span>
                      <p className="text-sm font-semibold text-sky-900 mt-1">
                        {(quickscanDetails.total_kwh || 0).toLocaleString('nl-NL')} kWh
                      </p>
                    </div>
                    <div className="bg-cyan-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-cyan-600">Totaal ERE</span>
                      <p className="text-sm font-semibold text-cyan-900 mt-1">
                        {(quickscanDetails.total_ere || 0).toFixed(4)}
                      </p>
                    </div>
                    <div className="bg-teal-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-teal-600">ERE uit net</span>
                      <p className="text-sm font-semibold text-teal-900 mt-1">
                        {(quickscanDetails.ere_uit_net || 0).toFixed(4)}
                      </p>
                    </div>
                    <div className="bg-emerald-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-emerald-600">ERE hernieuwbaar</span>
                      <p className="text-sm font-semibold text-emerald-900 mt-1">
                        {(quickscanDetails.ere_hernieuwbaar || 0).toFixed(4)}
                      </p>
                    </div>
                    <div className="bg-lime-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-lime-600">Opbrengst ERE</span>
                      <p className="text-sm font-semibold text-lime-900 mt-1">
                        €{(quickscanDetails.opbrengst_ere || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                  
                  {/* Total Benefit - Highlighted */}
                  <div className="bg-gradient-to-r from-green-100 to-green-50 p-6 rounded-lg border-2 border-green-400">
                    <div className="text-center">
                      <span className="text-lg font-medium text-green-700">💰 Totaal Voordeel</span>
                      <p className="text-4xl font-bold text-green-900 mt-2">
                        €{(quickscanDetails.total_voordeel || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-sm text-green-700 mt-1">per jaar</p>
                    </div>
                  </div>
                </div>

                {/* Metadata */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Metadata</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-gray-600">Aangemaakt</span>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {format(new Date(quickscanDetails.created_at), 'dd MMM yyyy HH:mm', { locale: nl })}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-gray-600">Bijgewerkt</span>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {format(new Date(quickscanDetails.updated_at), 'dd MMM yyyy HH:mm', { locale: nl })}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            </>
          )}

          {/* Full Width Comprehensive Intake Quickscan Section */}
          {lead.origin_type === 'intake_quickscan' && quickscanDetails && (
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-lg">Volledige Intake Quickscan Rapportage</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Identification & Status */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Unieke identificatie en status</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-slate-600">Scan ID</span>
                      <p className="text-sm font-mono text-slate-900 mt-1">{quickscanDetails.scan_id}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-slate-600">Record ID</span>
                      <p className="text-sm font-mono text-slate-900 mt-1">{quickscanDetails.id}</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-blue-600">Status</span>
                      <Badge className="mt-1" variant={quickscanDetails.status === 'completed' ? 'default' : 'secondary'}>
                        {quickscanDetails.status}
                      </Badge>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-green-600">Voortgang</span>
                      <p className="text-sm font-semibold text-green-900 mt-1">
                        Stap {quickscanDetails.progress_step || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {quickscanDetails.submitted_at && (
                      <div className="bg-purple-50 p-3 rounded-lg">
                        <span className="text-xs font-medium text-purple-600">Ingediend</span>
                        <p className="text-sm font-semibold text-purple-900 mt-1">
                          {format(new Date(quickscanDetails.submitted_at), 'dd MMM yyyy HH:mm', { locale: nl })}
                        </p>
                      </div>
                    )}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-gray-600">Aangemaakt</span>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {format(new Date(quickscanDetails.created_at), 'dd MMM yyyy HH:mm', { locale: nl })}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-gray-600">Bijgewerkt</span>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {format(new Date(quickscanDetails.updated_at), 'dd MMM yyyy HH:mm', { locale: nl })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Contactgegevens</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-indigo-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-indigo-600">Contactpersoon</span>
                      <p className="text-sm font-semibold text-indigo-900 mt-1">
                        {quickscanDetails.contact_name || 'Niet opgegeven'}
                      </p>
                    </div>
                    <div className="bg-cyan-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-cyan-600">Email</span>
                      <p className="text-sm font-semibold text-cyan-900 mt-1">
                        {quickscanDetails.email || 'Niet opgegeven'}
                      </p>
                    </div>
                    <div className="bg-teal-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-teal-600">Telefoon</span>
                      <p className="text-sm font-semibold text-teal-900 mt-1">
                        {quickscanDetails.phone || 'Niet opgegeven'}
                      </p>
                    </div>
                    <div className="bg-emerald-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-emerald-600">Beste tijd</span>
                      <p className="text-sm font-semibold text-emerald-900 mt-1">
                        {labelFor(FIELD_LABELS.best_time, quickscanDetails.best_time)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Company Information */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Bedrijfsinformatie</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-blue-600">Bedrijfsnaam</span>
                      <p className="text-sm font-semibold text-blue-900 mt-1">
                        {quickscanDetails.company_name || 'Niet opgegeven'}
                      </p>
                    </div>
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-orange-600">Rol</span>
                      <p className="text-sm font-semibold text-orange-900 mt-1">
                        {labelFor(FIELD_LABELS.role, quickscanDetails.role)}
                      </p>
                    </div>
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-yellow-600">Branche</span>
                      <p className="text-sm font-semibold text-yellow-900 mt-1">
                        {labelFor(FIELD_LABELS.branch, quickscanDetails.branch)}
                      </p>
                    </div>
                    <div className="bg-amber-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-amber-600">Regio</span>
                      <p className="text-sm font-semibold text-amber-900 mt-1">
                        {labelFor(FIELD_LABELS.region, quickscanDetails.region)}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-red-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-red-600">Bedrijfsgrootte</span>
                      <p className="text-sm font-semibold text-red-900 mt-1">
                        {labelFor(FIELD_LABELS.company_size, quickscanDetails.company_size)}
                      </p>
                    </div>
                    <div className="bg-pink-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-pink-600">Lead Band</span>
                      <p className="text-sm font-semibold text-pink-900 mt-1">
                        {quickscanDetails.lead_band || 'Niet opgegeven'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Problem Experience & Technology */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Probleemervaring en techniek</h3>
                  <div className="space-y-3">
                    <div className="bg-rose-50 p-4 rounded-lg">
                      <span className="text-sm font-medium text-rose-600">Knelpunten</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(quickscanDetails.pain_points || []).map((point: string, index: number) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {labelFor(FIELD_LABELS.pain_points, point)}
                          </Badge>
                        ))}
                        {(!quickscanDetails.pain_points || quickscanDetails.pain_points.length === 0) && (
                          <span className="text-sm text-gray-500">Geen knelpunten opgegeven</span>
                        )}
                      </div>
                    </div>
                    <div className="bg-violet-50 p-4 rounded-lg">
                      <span className="text-sm font-medium text-violet-600">Aansluittype</span>
                      <p className="text-sm font-semibold text-violet-900 mt-1">
                        {labelFor(FIELD_LABELS.connection_type, quickscanDetails.connection_type)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Flex Potential */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Flexpotentie</h3>
                  <div className="bg-sky-50 p-4 rounded-lg">
                    <span className="text-sm font-medium text-sky-600">Flexibiliteitsopties</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(quickscanDetails.flex_options || []).map((option: string, index: number) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {labelFor(FIELD_LABELS.flex_options, option)}
                        </Badge>
                      ))}
                      {(!quickscanDetails.flex_options || quickscanDetails.flex_options.length === 0) && (
                        <span className="text-sm text-gray-500">Geen flexibiliteitsopties opgegeven</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Scoring & Assessment */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Beoordeling en scoring</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-green-600">Score (1-10)</span>
                      <p className="text-2xl font-bold text-green-900 mt-1">
                        {quickscanDetails.score_10 !== null ? quickscanDetails.score_10 : 'N/A'}
                      </p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-blue-600">Volgende actie</span>
                      <p className="text-sm font-semibold text-blue-900 mt-1">
                        {quickscanDetails.next_action || 'Niet opgegeven'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Urgency & Readiness */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Urgentie & bereidheid</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="bg-lime-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-lime-600">Urgentie</span>
                      <p className="text-sm font-semibold text-lime-900 mt-1">
                        {labelFor(FIELD_LABELS.urgency, quickscanDetails.urgency)}
                      </p>
                    </div>
                    <div className="bg-emerald-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-emerald-600">Data beschikbaar</span>
                      <p className="text-sm font-semibold text-emerald-900 mt-1">
                        {labelFor(FIELD_LABELS.data_available, quickscanDetails.data_available)}
                      </p>
                    </div>
                    <div className="bg-teal-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-teal-600">Budget gereed</span>
                      <p className="text-sm font-semibold text-teal-900 mt-1">
                        {labelFor(FIELD_LABELS.budget_ready, quickscanDetails.budget_ready)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                {(quickscanDetails.free_text || quickscanDetails.meta) && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-gray-900 border-b pb-1">Aanvullende informatie</h3>
                    {quickscanDetails.free_text && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <span className="text-sm font-medium text-gray-600">Vrije tekst</span>
                        <p className="text-sm text-gray-900 mt-2 whitespace-pre-wrap">
                          {quickscanDetails.free_text}
                        </p>
                      </div>
                    )}
                    {quickscanDetails.meta && Object.keys(quickscanDetails.meta).length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <span className="text-sm font-medium text-gray-600">Metadata</span>
                        <pre className="text-xs text-gray-900 mt-2 whitespace-pre-wrap bg-white p-2 rounded border overflow-x-auto">
                          {JSON.stringify(quickscanDetails.meta, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Regular Quickscan Details Section */}
          {lead.origin_type === 'quickscan' && quickscanDetails && (
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-lg">Volledige Quickscan Rapportage</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Company & Results Overview */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Bedrijfs- en resultaatoverzicht</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-blue-600">Bedrijfsnaam</span>
                      <p className="text-sm font-semibold text-blue-900 mt-1">
                        {quickscanDetails.company_name || 'Niet opgegeven'}
                      </p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-green-600">Email</span>
                      <p className="text-sm font-semibold text-green-900 mt-1">
                        {quickscanDetails.email || 'Niet opgegeven'}
                      </p>
                    </div>
                    <div className="bg-amber-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-amber-600">Bedrijfstype</span>
                      <p className="text-sm font-semibold text-amber-900 mt-1">
                        {quickscanDetails.business_type}
                      </p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-purple-600">Werknemers</span>
                      <p className="text-sm font-semibold text-purple-900 mt-1">
                        {quickscanDetails.employee_count}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Energy Data */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Energiegegevens</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="bg-red-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-red-600">Maandelijse energiekosten</span>
                      <p className="text-sm font-semibold text-red-900 mt-1">
                        €{quickscanDetails.monthly_energy_costs?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-yellow-600">Geschat PV-opwekking</span>
                      <p className="text-sm font-semibold text-yellow-900 mt-1">
                        {quickscanDetails.estimated_pv_generation?.toLocaleString() || 'N/A'} kWh/jaar
                      </p>
                    </div>
                    <div className="bg-cyan-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-cyan-600">Geschat energieverbruik</span>
                      <p className="text-sm font-semibold text-cyan-900 mt-1">
                        {quickscanDetails.estimated_kwh_usage?.toLocaleString() || 'N/A'} kWh/jaar
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-teal-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-teal-600">Aansluitcapaciteit levering</span>
                      <p className="text-sm font-semibold text-teal-900 mt-1">
                        {quickscanDetails.connection_capacity_delivery || 'N/A'} kW
                      </p>
                    </div>
                    <div className="bg-indigo-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-indigo-600">Aansluitcapaciteit teruglevering</span>
                      <p className="text-sm font-semibold text-indigo-900 mt-1">
                        {quickscanDetails.connection_capacity_return || 'N/A'} kW
                      </p>
                    </div>
                  </div>
                </div>

                {/* Results & Potential */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900 border-b pb-1">Quickscan resultaten</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-emerald-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-emerald-600">Energiepotentieel Score</span>
                      <p className="text-sm font-semibold text-emerald-900 mt-1">
                        {quickscanDetails.energy_potential_score || 'N/A'}%
                      </p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-green-600">Jaarlijkse besparing</span>
                      <p className="text-sm font-semibold text-green-900 mt-1">
                        €{quickscanDetails.yearly_annual_savings?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                    <div className="bg-lime-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-lime-600">CO2 reductie</span>
                      <p className="text-sm font-semibold text-lime-900 mt-1">
                        {quickscanDetails.co2_reduction || 'N/A'} ton/jaar
                      </p>
                    </div>
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <span className="text-xs font-medium text-orange-600">Terugverdientijd</span>
                      <p className="text-sm font-semibold text-orange-900 mt-1">
                        {quickscanDetails.roi_months || 'N/A'} maanden
                      </p>
                    </div>
                  </div>
                </div>

                {/* Peak Hours */}
                {quickscanDetails.peak_hours && quickscanDetails.peak_hours.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-gray-900 border-b pb-1">Piekuren energiegebruik</h3>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="flex flex-wrap gap-2">
                        {quickscanDetails.peak_hours.map((hour: any, index: number) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {hour.peak_hour}:00
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Current Solutions */}
                {quickscanDetails.current_solutions && quickscanDetails.current_solutions.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-gray-900 border-b pb-1">Huidige oplossingen</h3>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex flex-wrap gap-2">
                        {quickscanDetails.current_solutions.map((solution: any, index: number) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {solution.solution}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Action Planning Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Actie Planning</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setIsEditing(!isEditing)}
                >
                  {isEditing ? 'Annuleren' : 'Bewerken'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Volgende actie datum:</label>
                <Input
                  type="date"
                  value={nextActionDate}
                  onChange={(e) => setNextActionDate(e.target.value)}
                  disabled={!isEditing}
                  className="mt-1"
                />
              </div>
              
              {isEditing && (
                <Button onClick={handleSaveDetails} className="w-full">
                  <Save className="h-4 w-4 mr-2" />
                  Opslaan
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeadDetailModal;
